import { useState } from "react";
import { Link } from "react-router-dom";
import SEO, { getServiceSchema, getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Clock,
  Shield,
  TrendingUp,
  Home,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Sparkles,
  Users,
  BarChart3,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GetFreeAnalysis = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    property_address: "",
    property_city: "",
    property_zip: "",
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    currently_rented: "",
    current_rent: "",
    current_situation: "",
    biggest_challenge: "",
    timeline: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.property_address) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const message = [
        `Property: ${formData.property_address}, ${formData.property_city} ${formData.property_zip}`,
        `Type: ${formData.property_type || "Not specified"}`,
        `Beds: ${formData.bedrooms || "N/A"} | Baths: ${formData.bathrooms || "N/A"}`,
        `Currently Rented: ${formData.currently_rented || "Not specified"}`,
        formData.current_rent ? `Current Rent: $${formData.current_rent}/mo` : "",
        `Situation: ${formData.current_situation || "Not specified"}`,
        `Timeline: ${formData.timeline || "Not specified"}`,
        formData.biggest_challenge ? `Challenge: ${formData.biggest_challenge}` : "",
        `Phone: ${formData.phone}`,
      ]
        .filter(Boolean)
        .join("\n");

      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: message,
        form_type: "Free Property Analysis",
      });

      setIsSubmitted(true);
      toast.success("Your analysis request has been submitted!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again or call us at 913.795.1155");
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: BarChart3,
      title: "Market Rent Analysis",
      desc: "See what your property could earn based on real-time market data",
    },
    {
      icon: TrendingUp,
      title: "ROI Optimization Tips",
      desc: "Get personalized recommendations to maximize your rental income",
    },
    {
      icon: Shield,
      title: "Risk Assessment",
      desc: "Identify potential issues and how professional management mitigates them",
    },
    {
      icon: Users,
      title: "Tenant Demand Insight",
      desc: "Understand current renter demand in your specific neighborhood",
    },
  ];

  const trustPoints = [
    "No obligation, 100% free",
    "Personalized to your property",
    "We respond within 24 hours",
    "Local Johnson County experts",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Get Free Property Analysis | Rently Properties Kansas City"
        description="Get a free rental property analysis for your Johnson County home. Discover your property's earning potential in Overland Park, Leawood, Lenexa & Olathe. No obligation."
        canonical="/get-free-analysis"
        keywords="free property analysis Kansas City, rental property value, Overland Park rent estimate, Johnson County property management, free consultation"
        schema={[
          getServiceSchema(
            "Free Property Analysis",
            "Complimentary rental property analysis for homes in Johnson County, Kansas. Includes market rent estimate, ROI optimization tips, and personalized management recommendations."
          ),
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Get Free Analysis", path: "/get-free-analysis" },
          ]),
        ]}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-6 pb-10 sm:pt-10 sm:pb-14">
        <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-5">
                <Sparkles className="w-4 h-4" />
                Free, No-Obligation Analysis
              </span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
                Discover Your Property's{" "}
                <span className="text-primary">True Earning Potential</span>
              </h1>
              <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto font-medium leading-relaxed">
                Get a personalized analysis of your Johnson County rental property.
                We'll show you exactly how much more you could be earning — and reach out within 24 hours.
              </p>
            </motion.div>
          </div>

          {/* Trust Points */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-10"
          >
            {trustPoints.map((point, i) => (
              <div key={i} className="flex items-center gap-1.5 text-sm text-foreground/70">
                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="font-medium">{point}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Main Content: Form + Benefits */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 items-start">

            {/* Left: Benefits + Social Proof */}
            <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">

              {/* What You'll Get */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <h2 className="font-display text-xl font-bold text-foreground mb-5">
                  What You'll Receive:
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">{benefit.title}</h3>
                        <p className="text-sm text-foreground/60 mt-0.5">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Promise Box */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <Card className="border-primary/20 bg-primary/[0.03]">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <h3 className="font-display font-bold text-foreground">Our Promise to You</h3>
                    </div>
                    <p className="text-sm text-foreground/70 leading-relaxed">
                      A member of our team will personally review your property details and
                      reach out within <strong className="text-foreground">24 hours</strong> with
                      a custom analysis. No automated emails — a real conversation with a local
                      Johnson County property expert.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Quick Contact */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="space-y-3"
              >
                <h3 className="font-display text-sm font-bold text-foreground/60 uppercase tracking-wider">
                  Prefer to talk now?
                </h3>
                <a
                  href="tel:913-795-1155"
                  className="flex items-center gap-3 text-foreground hover:text-primary transition-colors duration-200"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">913.795.1155</p>
                    <p className="text-xs text-foreground/50">Mon-Fri 8am-6pm</p>
                  </div>
                </a>
                <a
                  href="mailto:Info@rently.properties"
                  className="flex items-center gap-3 text-foreground hover:text-primary transition-colors duration-200"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold">Info@rently.properties</p>
                    <p className="text-xs text-foreground/50">We reply fast</p>
                  </div>
                </a>
              </motion.div>
            </div>

            {/* Right: The Form */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Card className="shadow-[0_10px_40px_rgba(15,42,74,0.10)]">
                      <CardContent className="py-16 text-center" data-testid="analysis-form-success">
                        <div className="flex justify-center mb-6">
                          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-10 h-10 text-primary" />
                          </div>
                        </div>
                        <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
                          You're All Set!
                        </h2>
                        <p className="text-lg text-foreground/70 mb-2 max-w-md mx-auto">
                          We've received your property details and our team is already on it.
                        </p>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-6">
                          <Clock className="w-4 h-4" />
                          Expect to hear from us within 24 hours
                        </div>
                        <p className="text-sm text-foreground/50 mb-8">
                          Can't wait? Call us at{" "}
                          <a href="tel:913-795-1155" className="text-primary font-semibold hover:underline">
                            913.795.1155
                          </a>
                        </p>
                        <Button variant="outline" asChild>
                          <Link to="/" className="gap-2">
                            <Home className="w-4 h-4" />
                            Back to Homepage
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                  >
                    <Card className="shadow-[0_10px_40px_rgba(15,42,74,0.10)]">
                      <CardContent className="p-6 sm:p-8">
                        <div className="mb-6">
                          <h2 className="font-display text-xl font-bold text-foreground mb-1">
                            Tell Us About Your Property
                          </h2>
                          <p className="text-sm text-foreground/60">
                            Takes about 2 minutes. All information is kept confidential.
                          </p>
                        </div>

                        <form onSubmit={handleSubmit} data-testid="analysis-form" className="space-y-5">

                          {/* Section: Your Info */}
                          <div className="space-y-4">
                            <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Your Information</p>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label htmlFor="name">Full Name <span className="text-destructive">*</span></Label>
                                <Input
                                  id="name"
                                  name="name"
                                  data-testid="analysis-name-input"
                                  placeholder="John Smith"
                                  value={formData.name}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="phone">Phone Number <span className="text-destructive">*</span></Label>
                                <Input
                                  id="phone"
                                  name="phone"
                                  type="tel"
                                  data-testid="analysis-phone-input"
                                  placeholder="(913) 555-0123"
                                  value={formData.phone}
                                  onChange={handleChange}
                                  required
                                />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="email">Email Address <span className="text-destructive">*</span></Label>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                data-testid="analysis-email-input"
                                placeholder="john@example.com"
                                value={formData.email}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Section: Property Details */}
                          <div className="space-y-4 pt-2">
                            <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Property Details</p>

                            <div className="space-y-1.5">
                              <Label htmlFor="property_address">Property Address <span className="text-destructive">*</span></Label>
                              <Input
                                id="property_address"
                                name="property_address"
                                data-testid="analysis-address-input"
                                placeholder="123 Main Street"
                                value={formData.property_address}
                                onChange={handleChange}
                                required
                              />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label htmlFor="property_city">City</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("property_city", val)}
                                >
                                  <SelectTrigger data-testid="analysis-city-select">
                                    <SelectValue placeholder="Select city" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Overland Park">Overland Park</SelectItem>
                                    <SelectItem value="Leawood">Leawood</SelectItem>
                                    <SelectItem value="Lenexa">Lenexa</SelectItem>
                                    <SelectItem value="Olathe">Olathe</SelectItem>
                                    <SelectItem value="Shawnee">Shawnee</SelectItem>
                                    <SelectItem value="Prairie Village">Prairie Village</SelectItem>
                                    <SelectItem value="Mission">Mission</SelectItem>
                                    <SelectItem value="Merriam">Merriam</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="property_zip">Zip Code</Label>
                                <Input
                                  id="property_zip"
                                  name="property_zip"
                                  data-testid="analysis-zip-input"
                                  placeholder="66211"
                                  value={formData.property_zip}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-1.5">
                                <Label>Property Type</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("property_type", val)}
                                >
                                  <SelectTrigger data-testid="analysis-type-select">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Single Family">Single Family</SelectItem>
                                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                                    <SelectItem value="Condo">Condo</SelectItem>
                                    <SelectItem value="Multi-Family">Multi-Family</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label>Bedrooms</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("bedrooms", val)}
                                >
                                  <SelectTrigger data-testid="analysis-beds-select">
                                    <SelectValue placeholder="Beds" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">1</SelectItem>
                                    <SelectItem value="2">2</SelectItem>
                                    <SelectItem value="3">3</SelectItem>
                                    <SelectItem value="4">4</SelectItem>
                                    <SelectItem value="5+">5+</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label>Bathrooms</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("bathrooms", val)}
                                >
                                  <SelectTrigger data-testid="analysis-baths-select">
                                    <SelectValue placeholder="Baths" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">1</SelectItem>
                                    <SelectItem value="1.5">1.5</SelectItem>
                                    <SelectItem value="2">2</SelectItem>
                                    <SelectItem value="2.5">2.5</SelectItem>
                                    <SelectItem value="3+">3+</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          {/* Section: Current Situation */}
                          <div className="space-y-4 pt-2">
                            <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Current Situation</p>

                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label>Is it currently rented?</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("currently_rented", val)}
                                >
                                  <SelectTrigger data-testid="analysis-rented-select">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Yes">Yes, currently rented</SelectItem>
                                    <SelectItem value="No">No, it's vacant</SelectItem>
                                    <SelectItem value="Soon">Tenant leaving soon</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor="current_rent">Current Monthly Rent</Label>
                                <Input
                                  id="current_rent"
                                  name="current_rent"
                                  data-testid="analysis-rent-input"
                                  placeholder="e.g. 1800"
                                  value={formData.current_rent}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-1.5">
                                <Label>How are you managing it?</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("current_situation", val)}
                                >
                                  <SelectTrigger data-testid="analysis-situation-select">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Self-Managing">Self-Managing</SelectItem>
                                    <SelectItem value="Another PM Company">Using Another PM Company</SelectItem>
                                    <SelectItem value="New Property">New Property / Not Yet Rented</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label>How soon do you want to start?</Label>
                                <Select
                                  onValueChange={(val) => handleSelectChange("timeline", val)}
                                >
                                  <SelectTrigger data-testid="analysis-timeline-select">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Immediately">Immediately</SelectItem>
                                    <SelectItem value="Within 30 days">Within 30 days</SelectItem>
                                    <SelectItem value="Just exploring">Just exploring options</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1.5">
                              <Label htmlFor="biggest_challenge">What's your biggest challenge? <span className="text-foreground/40">(optional)</span></Label>
                              <Textarea
                                id="biggest_challenge"
                                name="biggest_challenge"
                                data-testid="analysis-challenge-textarea"
                                placeholder="e.g. Finding reliable tenants, handling maintenance calls, maximizing rent..."
                                rows={3}
                                value={formData.biggest_challenge}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          {/* Submit */}
                          <div className="pt-2">
                            <Button
                              type="submit"
                              size="lg"
                              data-testid="analysis-form-submit-button"
                              disabled={isSubmitting}
                              className="w-full text-lg px-8 py-6 h-auto gap-2"
                            >
                              {isSubmitting ? (
                                "Submitting..."
                              ) : (
                                <>
                                  Get My Free Analysis
                                  <ArrowRight className="w-5 h-5" />
                                </>
                              )}
                            </Button>
                            <p className="text-xs text-center text-foreground/40 mt-3">
                              Your information is secure and will never be shared. We'll reach out within 24 hours.
                            </p>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GetFreeAnalysis;
